import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth-context';
import { holidayAPI, branchAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash2, CalendarDays } from 'lucide-react';

export default function HolidaysPage() {
  const { isAdmin, user } = useAuth();
  const [holidays, setHolidays] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: '', date: '' });

  useEffect(() => { loadBranches(); }, []);

  useEffect(() => {
    if (selectedBranch) loadHolidays();
  }, [selectedBranch, selectedYear]);

  const loadBranches = async () => {
    try {
      const res = await branchAPI.list();
      setBranches(res.data);
      if (res.data.length > 0) {
        setSelectedBranch(user?.branch_id || res.data[0].id);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadHolidays = async () => {
    try {
      const res = await holidayAPI.list(selectedBranch, selectedYear);
      setHolidays(res.data);
    } catch (e) { console.error(e); }
  };

  const handleAdd = async () => {
    if (!form.name || !form.date) { toast.error('Name and date are required'); return; }
    try {
      await holidayAPI.create({
        branch_id: selectedBranch,
        name: form.name,
        date: form.date,
        year: selectedYear
      });
      toast.success('Holiday added');
      setDialogOpen(false);
      setForm({ name: '', date: '' });
      loadHolidays();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add holiday');
    }
  };

  const handleDelete = async (id) => {
    try {
      await holidayAPI.delete(id);
      toast.success('Holiday removed');
      loadHolidays();
    } catch { toast.error('Delete failed'); }
  };

  const branchName = branches.find(b => b.id === selectedBranch)?.name || '';

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-semibold tracking-tight">Holiday Calendar</h1>
          <p className="text-muted-foreground">Manage government and company holidays per branch</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setDialogOpen(true)} className="gap-2" data-testid="add-holiday-button">
            <Plus size={16} /> Add Holiday
          </Button>
        )}
      </div>

      <div className="flex gap-3">
        <Select value={selectedBranch} onValueChange={setSelectedBranch}>
          <SelectTrigger className="w-[220px]">
            <SelectValue placeholder="Select branch" />
          </SelectTrigger>
          <SelectContent>
            {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={String(selectedYear)} onValueChange={v => setSelectedYear(Number(v))}>
          <SelectTrigger className="w-[120px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[2026, 2027, 2028].map(y => (
              <SelectItem key={y} value={String(y)}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <CalendarDays size={20} className="text-primary" />
            {branchName} - {selectedYear} Holidays ({holidays.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {holidays.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No holidays configured for {selectedYear}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Holiday Name</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Day</TableHead>
                  {isAdmin && <TableHead>Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {holidays.map((h, idx) => {
                  const dateObj = new Date(h.date + 'T00:00:00');
                  const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'long' });
                  return (
                    <TableRow key={h.id}>
                      <TableCell className="text-muted-foreground">{idx + 1}</TableCell>
                      <TableCell className="font-medium">{h.name}</TableCell>
                      <TableCell className="tabular-nums">{h.date}</TableCell>
                      <TableCell className="text-muted-foreground">{dayName}</TableCell>
                      {isAdmin && (
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(h.id)} className="text-destructive hover:text-destructive">
                            <Trash2 size={14} />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Add Holiday</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Holiday Name</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g., Republic Day" data-testid="holiday-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Date</Label>
              <Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} data-testid="holiday-date-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAdd} data-testid="holiday-save-button">Add Holiday</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
